import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import SbarNote from '../components/SbarNote';
import DischargeNote from '../components/DischargeNote';
import NoteAddendumsList from '../components/NoteAddendumsList';
import { generatePDF } from '../services/pdfGenerator';
import { generateArabicPatientCopy } from '../services/openAIService';
import { downloadArabicPatientCopyPdf } from '../services/arabicPatientCopyPdf';
import { ROUTES } from '../constants/routes';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getNoteAddendums } from '../utils/addendum';
import { getReportTypeLabel } from '../i18n/translations';

export default function ReportDetailScreen() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionNotes } = useSession();
  const { t } = useLanguage();
  const reportFromState = location.state?.report;
  const liveNote = reportFromState?.note?.id
    ? sessionNotes.find((n) => n.id === reportFromState.note.id)
    : null;
  const report = liveNote
    ? {
        ...reportFromState,
        note: liveNote,
        datetime: liveNote.datetime,
      }
    : reportFromState;
  const [arabicBusy, setArabicBusy] = useState(false);
  const [arabicError, setArabicError] = useState('');

  if (!report) {
    return (
      <div className="p-8 text-center text-gray-500">{t('noReportsYet')}</div>
    );
  }

  const savedNote = report.note;
  const note = savedNote?.openaiNote || savedNote?.note_content;
  const noteType = savedNote?.note_type || report.type;
  const datetime = new Date(report.datetime);
  const patientName = report.patientName || savedNote?.patient_name || 'Unknown';
  const isDischarge = report.type === 'discharge';

  const handlePdf = () => {
    if (!note) return;
    generatePDF({
      note,
      noteType: noteType === 'shift_handover' ? 'handover' : noteType,
      patientName,
      room: savedNote?.patient_room || '—',
      datetime,
      addendums: getNoteAddendums(savedNote),
    });
  };

  const handleArabicPatientCopy = async () => {
    if (!note || !isDischarge) return;
    setArabicBusy(true);
    setArabicError('');
    try {
      const copy = await generateArabicPatientCopy(note);
      await downloadArabicPatientCopyPdf(copy, patientName);
    } catch (err) {
      setArabicError(err.message || 'Arabic patient copy generation failed');
    } finally {
      setArabicBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-page pb-36">
      <header className="sticky top-0 border-b border-gray-200 bg-surface px-4 py-3">
        <button type="button" onClick={() => navigate(ROUTES.REPORTS)} className="flex items-center gap-2 text-primary">
          <ArrowLeft className="h-5 w-5" />
          {t('back')}
        </button>
        <h1 className="mt-2 text-lg font-bold">{report.title}</h1>
        <p className="text-sm text-gray-500">{getReportTypeLabel(t, report.type)}</p>
      </header>

      <div className="px-4 py-4">
        {report.type === 'incident' && (
          <p className="text-sm text-gray-600">Incident report placeholder for {id}</p>
        )}
        {report.type === 'shift_handover' && note && (
          <SbarNote note={note} onChange={() => {}} datetime={datetime} />
        )}
        {isDischarge && note && (
          <DischargeNote note={note} onChange={() => {}} readOnly />
        )}
        {savedNote && <NoteAddendumsList note={savedNote} />}
      </div>

      {note && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {arabicError ? (
            <p className="mb-2 text-center text-sm text-critical">{arabicError}</p>
          ) : null}
          <div className="flex items-center justify-center gap-3">
            {isDischarge && (
              <button
                type="button"
                onClick={handleArabicPatientCopy}
                disabled={arabicBusy}
                className="btn-secondary px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
              >
                {arabicBusy ? t('generating') : t('patientCopyArabic')}
              </button>
            )}
            <button
              type="button"
              onClick={handlePdf}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white"
              aria-label="Download PDF"
            >
              <Download className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
